import React, { useState, useEffect } from 'react';
import { Form, Button, Container, Row, Col, Alert } from 'react-bootstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Payment } from '../components/Payment';

//Input sanitization utility
const sanitizeInput = (input) => {
    if (typeof input !== 'string') return '';
    return input
        .replace(/<[^>]*>/g, '') // Remove HTML tags
        .replace(/[<>]/g, '') // Remove angle brackets
        .replace(/javascript:/gi, '') // Remove javascript: protocol
        .replace(/on\w+=/gi, '') // Remove event handlers
        .trim();
};

// Password strength validation
const validatePassword = (password) => {
    const errors = [];
    if (password.length < 8) {
        errors.push('Password must be at least 8 characters long');
    }
    
    if (!/[A-Z]/.test(password)) {
        errors.push('Password must contain at least one uppercase letter');
    }
    
    if (!/[a-z]/.test(password)) {
        errors.push('Password must contain at least one lowercase letter');
    }
    
    if (!/\d/.test(password)) {
        errors.push('Password must contain at least one number');
    }
    
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
        errors.push('Password must contain at least one special character (!@#$%^&*(),.?:{}|<>)');
    }
    
    return { isValid: errors.length === 0, errors };
};

// Email validation
const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
};

// CSRF token management
let csrfToken = null;

const fetchCSRFToken = async () => {
    try {
        const response = await fetch('/api/v1/csrf/token', {
            method: 'GET',
            credentials: 'include',
        });
        
        if (!response.ok) {
            throw new Error('Failed to fetch CSRF token');
        }
        
        const data = await response.json();
        csrfToken = data.csrfToken;
        return csrfToken;
    } catch (error) {
        throw error;
    }
};

const Signup = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isGoogleLoading, setIsGoogleLoading] = useState(false);
    const [passwordErrors, setPasswordErrors] = useState([]);
    const [showPasswordRequirements, setShowPasswordRequirements] = useState(false);
    const location = useLocation();
    const [preferences, setPreferences] = useState(null);

    const navigate = useNavigate();
    const { session, signUpNewUser, signInWithGoogle } = useAuth();

    useEffect(() => {
        // Try to get preferences from router state first
        if (location.state?.preferences) {
            setPreferences(location.state.preferences);
        } 
        // Fall back to localStorage if router state is empty
        else {
            const storedPreferences = localStorage.getItem('tempUserPreferences');
            if (storedPreferences) {
                setPreferences(JSON.parse(storedPreferences));
            }
        }
        
        // Fetch CSRF token on component mount
        fetchCSRFToken().catch(error => {
            console.error('Failed to fetch CSRF token:', error);
        });
    }, [location]);

    // Handle password changes and validation
    const handlePasswordChange = (e) => {
        const newPassword = e.target.value;
        setPassword(newPassword);
        
        if (newPassword.length > 0) {
            const validation = validatePassword(newPassword);
            setPasswordErrors(validation.errors);
            setShowPasswordRequirements(true);
        } else {
            setPasswordErrors([]);
            setShowPasswordRequirements(false);
        }
    };

    // Handle input changes with sanitization
    const handleInputChange = (setter) => (e) => {
        const sanitizedValue = sanitizeInput(e.target.value);
        setter(sanitizedValue);
    };

    const handleSignUp = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        
        try {
            // Validate all inputs
            const sanitizedEmail = sanitizeInput(email);
            const sanitizedFirstName = sanitizeInput(firstName);
            const sanitizedLastName = sanitizeInput(lastName);
            
            // Email validation
            if (!validateEmail(sanitizedEmail)) {
                setError('Please enter a valid email address');
                setIsLoading(false);
                return;
            }
            
            // Name validation
            if (!sanitizedFirstName.trim() || sanitizedFirstName.length < 2) {
                setError('First name must be at least 2 characters long');
                setIsLoading(false);
                return;
            }
            
            if (!sanitizedLastName.trim() || sanitizedLastName.length < 2) {
                setError('Last name must be at least 2 characters long');
                setIsLoading(false);
                return;
            }
            
            // Password validation
            const passwordValidation = validatePassword(password);
            if (!passwordValidation.isValid) {
                setError('Please fix password requirements');
                setIsLoading(false);
                return;
            }
            
            // Ensure we have a CSRF token
            if (!csrfToken) {
                await fetchCSRFToken();
            }
            
            const result = await signUpNewUser(sanitizedEmail, password, sanitizedFirstName, sanitizedLastName);
            if (result.success) {
                handleSuccessfulSignup(result.data.user);
            } else {
                setError(result.error);
            }
        } catch (error) {
            setError('An error occurred during signup');
        } finally {
            setIsLoading(false);
        }
    }

    const handleGoogleSignUp = async () => {
        setIsGoogleLoading(true);
        setError('');
        
        try {
            const result = await signInWithGoogle();
            if (!result.success) {
                setError(result.error);
            }
            // No need to navigate - the OAuth redirect will handle this
        } catch (error) {
            setError('An error occurred during Google sign up');

        } finally {
            setIsGoogleLoading(false);
        }
    };

    const handleSuccessfulSignup = (userData) => {
        localStorage.removeItem('tempUserPreferences');
        
        // Store minimal user data temporarily for email confirmation page
        if (userData) {
            const minimalUserData = {
                id: userData.id,
                email: userData.email
            };
            sessionStorage.setItem('tempSignupUser', JSON.stringify(minimalUserData));
        }
        
        navigate('/email-confirmation');
    };

    return (
        <Container fluid className="py-5 px-3">
            <Row className="justify-content-center m-0">
                <Col md={6} lg={4}>
                    <div className="text-center mb-4">
                        <h2>Lets get cooking.</h2>
                    </div>
                    
                    {error && <Alert variant="danger">{error}</Alert>}
                    
                    <Form onSubmit={handleSignUp} className="auth-form">
                        <Button 
                            className="google-btn"
                            onClick={handleGoogleSignUp}
                            disabled={isGoogleLoading}
                            type="button"
                        >
                            {isGoogleLoading ? (
                                'Connecting to Google...'
                            ) : (
                                <div className="d-flex align-items-center justify-content-center">
                                    <img 
                                        src="/images/google_logo.png" 
                                        alt="Google logo" 
                                        style={{ width: '20px', height: '20px', marginRight: '10px' }} 
                                    />
                                    Continue with Google
                                </div>
                            )}
                        </Button>
                        
                        <div className="auth-divider">
                            <span>or</span>
                        </div>
                        
                        <Row>
                            <Col md={6} className="pe-md-2">
                                <Form.Group className="mb-3">
                                    <Form.Label className="d-block text-start">First Name</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={firstName}
                                        onChange={handleInputChange(setFirstName)}
                                        required
                                        placeholder="Peter"
                                        className="form-control-custom"
                                        minLength={2}
                                        maxLength={50}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6} className="ps-md-2">
                                <Form.Group className="mb-3">
                                    <Form.Label className="d-block text-start">Last Name</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={lastName}
                                        onChange={handleInputChange(setLastName)}
                                        required
                                        placeholder="Parker"
                                        className="form-control-custom"
                                        minLength={2}
                                        maxLength={50}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        <Form.Group className="mb-3">
                            <Form.Label className="d-block text-start">Email</Form.Label>
                            <Form.Control
                                type="email"
                                value={email}
                                onChange={handleInputChange(setEmail)}
                                required
                                placeholder="notspiderman@gmail.com"
                                className="form-control-custom"
                                maxLength={255}
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label className="d-block text-start">Password</Form.Label>
                            <Form.Control
                                type="password"
                                value={password}
                                onChange={handlePasswordChange}
                                required
                                placeholder="Enter a password"
                                className="form-control-custom"
                                minLength={8}
                                maxLength={128}
                            />
                            {showPasswordRequirements && (
                                <div className="mt-2">
                                    <small className="text-muted">Password requirements:</small>
                                    <ul className="list-unstyled small mt-1">
                                        <li className={password.length >= 8 ? 'text-success' : 'text-danger'}>
                                            ✓ At least 8 characters
                                        </li>
                                        <li className={/[A-Z]/.test(password) ? 'text-success' : 'text-danger'}>
                                            ✓ One uppercase letter
                                        </li>
                                        <li className={/[a-z]/.test(password) ? 'text-success' : 'text-danger'}>
                                            ✓ One lowercase letter
                                        </li>
                                        <li className={/\d/.test(password) ? 'text-success' : 'text-danger'}>
                                            ✓ One number
                                        </li>
                                        <li className={/[!@#$%^&*(),.?":{}|<>]/.test(password) ? 'text-success' : 'text-danger'}>
                                            ✓ One special character
                                        </li>
                                    </ul>
                                </div>
                            )}
                        </Form.Group>

                        <Button 
                            variant="primary" 
                            type="submit" 
                            className="auth-primary-btn mb-3"
                            disabled={isLoading || passwordErrors.length > 0}
                        >
                            {isLoading ? 'Signing up...' : 'Sign Up'}
                        </Button>

                        <div className="text-center">
                            Already have an account? <a href="/login" className="auth-link">Log in</a>
                        </div>
                    </Form>
                </Col>
            </Row>
        </Container>
    );
};

export default Signup;
